import React from 'react';

import { EEcommerceProvider } from '@domain/enums/common/integrations/EEcommerceProvider';

import { useStoreIntegrations } from '@helpers/hooks/pages/dashboard/integrationCenter/useStoreIntegrations';
import { useStoreConfig } from '@helpers/hooks/useStoreConfig';

import { getEcommerceProviderIntegrationStatus } from '@helpers/utils/dashboard/integrationsCenter/ecommerceProviders';

import { ECOMMERCE_PROVIDERS } from '@constants/common/integrations';

import IntegrationCard from '@components/Dashboard/IntegrationCenter/IntegrationCard';
import Text from '@components/common/core/DataDisplay/Text';
import SkeletonLoadingCard from '../../SkeletonLoadingCard';

import * as S from './styles';

const EcommerceProvider: React.FC = () => {
  const emptyArray = Array.from(Array(5).keys());

  const {
    searchIntegrationsValue,
    isSideModalOpen,
    handleSideModalOpen,
    handleSelectedIntegration,
    selectedIntegration,
  } = useStoreIntegrations();
  const { storeIntegrations, isLoadingStore, isValidatingStore } = useStoreConfig();

  const SideModalContent = ECOMMERCE_PROVIDERS.find(
    integration => integration.identifier === selectedIntegration,
  )?.sideModal;

  const onIntegrationCardClick = React.useCallback(
    (identifier: EEcommerceProvider) => {
      handleSideModalOpen();
      handleSelectedIntegration(identifier);
    },
    [handleSideModalOpen, handleSelectedIntegration],
  );

  const checkIsIntegrationCardDisabled = React.useCallback(
    (identifier: EEcommerceProvider): boolean => {
      const hasIntegration = storeIntegrations?.store_front.find(
        integration => integration.is_active,
      );

      if (!hasIntegration) return false;

      const foundStoreFrontIntegration = storeIntegrations?.store_front.find(
        integration => integration.name === identifier && integration.is_active,
      );

      if (foundStoreFrontIntegration) return false;

      return true;
    },
    [storeIntegrations],
  );

  if (isLoadingStore || isValidatingStore) {
    return (
      <S.EcommerceIntegrationWrapper>
        <S.Heading>Loja Virtual</S.Heading>

        <S.CardsWrapper>
          {emptyArray.map(element => (
            <SkeletonLoadingCard key={element} />
          ))}
        </S.CardsWrapper>
      </S.EcommerceIntegrationWrapper>
    );
  }

  const filteredDropshippingProvidersIntegrations = ECOMMERCE_PROVIDERS.filter(integration => {
    const integrationName = integration.name.toLowerCase();
    const searchIntegration = searchIntegrationsValue.toLowerCase();
    const isIntegrationIncludedOnSearch = integrationName.includes(searchIntegration);

    if (!isIntegrationIncludedOnSearch) return false;

    return true;
  });

  return (
    <S.EcommerceIntegrationWrapper>
      <S.Heading>Loja Virtual</S.Heading>

      <S.CardsWrapper>
        {filteredDropshippingProvidersIntegrations.map(filteredIntegration => {
          const isActivated = getEcommerceProviderIntegrationStatus(
            storeIntegrations,
            filteredIntegration.identifier,
          );

          return (
            <IntegrationCard
              type="providers"
              icon={filteredIntegration.icon}
              isActivated={isActivated}
              title={filteredIntegration.name}
              key={filteredIntegration.name}
              isDisabled={checkIsIntegrationCardDisabled(filteredIntegration.identifier)}
              onClick={() => onIntegrationCardClick(filteredIntegration.identifier)}
            />
          );
        })}

        {!filteredDropshippingProvidersIntegrations.length && (
          <Text>Não encontramos nenhuma integração que contenha esse nome.</Text>
        )}
      </S.CardsWrapper>

      {SideModalContent && (
        <S.SideModal isOpen={isSideModalOpen} toggle={handleSideModalOpen}>
          <SideModalContent />
        </S.SideModal>
      )}
    </S.EcommerceIntegrationWrapper>
  );
};

export default EcommerceProvider;
